class GloboProductCounter {
  constructor() {
    this.hideTimer = null;
    this.lastCount = null;
    this.isHidden = false;
    this.isUpdating = false;
    this.gridObserver = null;
    this.observedGrid = null;
    this.lockedCount = null;        // display count — locked after first stable calc
    this.isFilterChange = false;    // true when filter clicked — triggers recalculation
    this.stableTimer = null;        // debounce timer — wait for DOM to stop changing
    this.baselineCount = null;      // first count without filters — used when all filters cleared
    this.init();
  }

  init() {
    this.hideCounterFor2500ms();
    this.toggleClearButton();

    const setup = () => {
      this.observeGridChanges();
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', setup);
    } else {
      setup();
    }

    this.observeFilterClicks();

    // After Globo fires render complete, start watching for DOM stability
    window.addEventListener('globoFilterRenderCompleted', () => {
      this.observeGridChanges();
      this.scheduleStableCount();
    });
  }

  // ─── Stability: Wait for DOM to stop changing ────────────

  /**
   * Schedule a count update after DOM stops changing for 1.2 seconds.
   * Every new mutation or event resets the timer.
   * This ensures we only count when the grid is truly settled.
   */
  scheduleStableCount() {
    clearTimeout(this.stableTimer);
    this.stableTimer = setTimeout(() => {
      this.updateDisplayCount();
    }, 2500); // 2.5 Seconds delay to ensure DOM has perfectly settled
  }

  // ─── Data Sources ────────────────────────────────────────

  /**
   * Globo's native total from multiple possible sources:
   * 1. .gf-filter-result-count → "{{pagination.total}} Results" (in filter footer)
   * 2. .gf-summary → Globo may inject "Showing 1-24 of 150 results" (before we overwrite it)
   * Available on BOTH collection and search pages.
   */
  getGloboNativeCount() {
    // Source 1: filter footer count (most reliable)
    const footerEl = document.querySelector('.gf-filter-result-count');
    if (footerEl) {
      const match = footerEl.textContent.match(/(\d+)/);
      if (match && parseInt(match[1]) > 0) return parseInt(match[1]);
    }

    // Source 2: Globo's summary "Showing X of Y" or "X of Y"
    const summaryEl = document.querySelector('.gf-summary');
    if (summaryEl) {
      // Match "of 150" pattern
      const ofMatch = summaryEl.textContent.match(/of\s+(\d+)/i);
      if (ofMatch && parseInt(ofMatch[1]) > 0) return parseInt(ofMatch[1]);
      // Match standalone number if we haven't overwritten it yet
      const numMatch = summaryEl.textContent.match(/(\d+)/);
      if (numMatch && parseInt(numMatch[1]) > 0 && !summaryEl.textContent.includes('Results:')) {
        return parseInt(numMatch[1]);
      }
    }

    return null;
  }

  countVisibleCards() {
    return document.querySelectorAll('.grid__item .card-wrapper').length;
  }

  // ─── Display Count Update ───────────────────────────────

  /**
   * Called ONLY from globoFilterRenderCompleted + 800ms delay.
   * This ensures DOM has fully settled before we calculate.
   */
  updateDisplayCount() {
    const cards = this.countVisibleCards();

    // Dispatch for GloboEmptyState
    window.dispatchEvent(new CustomEvent('globoProductCountUpdate', {
      detail: { visibleCount: cards }
    }));

    // Prevent hiding the display if it's just sorting DOM flash
    if (cards === 0 && !this.lockedCount) {
      this.lastCount = 0;
      this.hideCounter();
      const rc = document.getElementById('resultCount');
      if (rc) { rc.style.visibility = 'hidden'; rc.style.opacity = '0'; }
      return;
    }

    // Check if filters are active
    const hasFilters = this.hasActiveFilters();
    const globoTotal = this.getGloboNativeCount();

    // No filters active → use baseline if available, otherwise calculate & save as baseline
    if (!hasFilters) {
      if (this.baselineCount !== null) {
        // All filters cleared → restore original count
        this.lockedCount = this.baselineCount;
        this.lastCount = this.baselineCount;
        this.displayCount(this.baselineCount);
        this.isFilterChange = false;
        this.toggleClearButton();
        return;
      }
      // First load (no filters yet) → calculate and save as baseline
      const finalCount = this.calculateVariantCount(cards);
      this.baselineCount = finalCount;
      this.lockedCount = finalCount;
      this.lastCount = finalCount;
      this.displayCount(finalCount);
      this.isFilterChange = false;
      this.toggleClearButton();
      return;
    }

    // Filters active → decide whether to recalculate or reuse locked count.
    // Super Powerful Lock: If lockedCount exists and no real filter changed, NEVER recalculate!
    const shouldRecalculate = this.isFilterChange || this.lockedCount === null;

    if (shouldRecalculate) {
      this.isFilterChange = false;
      const finalCount = this.calculateVariantCount(cards);
      this.lockedCount = finalCount;
      this.lastCount = finalCount;
      this.displayCount(finalCount);
    } else {
      // Collection page: sort or page change → reuse locked count
      this.lastCount = this.lockedCount;
      this.displayCount(this.lockedCount);
    }
    this.toggleClearButton();
  }

  /**
   * Calculate total result count.
   * - 1 page only  → all cards visible in DOM → use exact card count (by variant, accurate)
   * - >1 pages     → use Globo's native total (stable), fallback to cards × pages estimate
   */
  calculateVariantCount(cardsOnPage) {
    const globoTotal = this.getGloboNativeCount();
    const hasPagination = document.querySelector('.gf-pagination, .pagination') !== null;

    if (!hasPagination) {
      // Single page — we see everything → exact count
      return cardsOnPage;
    }

    // Multiple pages — use Globo's total (by product, but stable)
    if (globoTotal) {
      return globoTotal;
    }

    // Last resort — estimate from cards × pages
    let totalPages = 1;
    document.querySelectorAll('.gf-pagination a, .pagination a').forEach(link => {
      const num = parseInt(link.textContent.trim());
      if (!isNaN(num) && num > totalPages) totalPages = num;
    });
    return cardsOnPage * totalPages;
  }

  // ─── Display ─────────────────────────────────────────────

  displayCount(count) {
    this.isUpdating = true;

    const summary = document.querySelector('.gf-summary');
    if (summary) summary.innerHTML = `Results: ${count}`;

    const resultCount = document.getElementById('resultCount');
    if (resultCount) resultCount.innerHTML = count;

    // document.documentElement.style.setProperty(
    //   '--gf-results-count', `"${count} Results"`
    // );

     const filterFooter = document.querySelector(".gf-filter-footer");
    if (filterFooter) {
      // Set the CSS property so the native result count space shows up
      document.documentElement.style.setProperty("--gf-results-count", `"${count} Results"`);

      // Inject "Clear All" button, positioned absolutely over the ::before space
      filterFooter.style.position = 'relative'; // Ensure it's relative

      let clearAllBtn = filterFooter.querySelector('.gf-custom-clear-all');
      if (!clearAllBtn) {
        clearAllBtn = document.createElement("a");
        clearAllBtn.className = "gf-custom-clear-all gf-clear-all";
        clearAllBtn.innerHTML = "Clear All";

        clearAllBtn.addEventListener("click", (e) => {
          e.preventDefault();
          if (typeof handleClearAll === 'function') {
            handleClearAll();
          } else if (typeof clearAllFilter === 'function') {
            clearAllFilter();
          } else {
            const clearBtns = document.querySelectorAll(".gf-clear, .clear-link-button");
            clearBtns.forEach(btn => btn.click());
          }
        });
        filterFooter.appendChild(clearAllBtn);
      }

      // Show or hide based on active filters
      if (this.hasActiveFilters()) {
        clearAllBtn.style.setProperty('display', 'block', 'important');
      } else {
        clearAllBtn.style.setProperty('display', 'none', 'important');
      }
      
      // Fix: Force button to stay in the new DOM when Globo replaces the footer
      if (!clearAllBtn.parentElement || clearAllBtn.parentElement !== filterFooter) {
         filterFooter.appendChild(clearAllBtn);
      }
    }

    document.querySelectorAll('.gf-filter-footer button').forEach(btn => {
      btn.innerHTML = 'Done';
    });

    requestAnimationFrame(() => { this.isUpdating = false; });

    if (!this.isHidden) {
      if ((this.hasActiveFilters() || this.isSearchPage()) && count > 0) {
        this.showCounter();
      } else {
        this.hideCounter();
      }
    }
  }

  // ─── Visibility ──────────────────────────────────────────

  hideCounterFor2500ms() {
    this.isHidden = true;
    clearTimeout(this.hideTimer);

    const els = document.querySelectorAll('.gf-summary, .collection-result-count-text, #resultCount, .gf-filter-result-count');
    els.forEach(el => { el.style.visibility = 'hidden'; el.style.opacity = '0'; });

    // Specifically keep Clear All button visible during calculations if filters exist
    const clearAllBtn = document.querySelector('.gf-custom-clear-all');
    if (clearAllBtn && this.hasActiveFilters()) {
      clearAllBtn.style.setProperty('display', 'block', 'important');
    }

    this.hideTimer = setTimeout(() => {
      this.isHidden = false;
      if ((this.hasActiveFilters() || this.isSearchPage()) && this.lastCount > 0) {
        this.showCounter();
      }
    }, 3500); // Extended hidden window to 3.5s to wait for the 2.5s settled calculation
  }

  showCounter() {
    if (this.lastCount === 0) return;
    const els = document.querySelectorAll('.gf-summary, .collection-result-count-text, #resultCount, .gf-filter-result-count');
    els.forEach(el => { el.style.visibility = 'visible'; el.style.opacity = '1'; });
  }

  hideCounter() {
    const els = document.querySelectorAll('.gf-summary, .collection-result-count-text, #resultCount, .gf-filter-result-count');
    els.forEach(el => { el.style.visibility = 'hidden'; el.style.opacity = '0'; });

    // Specifically keep Clear All button visible during calculations if filters exist
    const clearAllBtn = document.querySelector('.gf-custom-clear-all');
    if (clearAllBtn && this.hasActiveFilters()) {
      clearAllBtn.style.setProperty('display', 'block', 'important');
    }
  }

  // ─── Helpers ─────────────────────────────────────────────

  hasActiveFilters() {
    return document.querySelectorAll('ul.globo-selected-items li.selected-item').length > 0;
  }

  isSearchPage() {
    return window.location.pathname.includes('/search') ||
      window.location.search.includes('q=') ||
      document.querySelector('.template-search') !== null;
  }

  toggleClearButton() {
    const clearBtn = document.querySelector('.mobile-clear-btn');
    const activeFilters = document.querySelectorAll('ul.globo-selected-items li.selected-item');
    if (clearBtn && window.innerWidth <= 825) {
      if (activeFilters.length > 0) {
        clearBtn.style.setProperty('display', 'inline-block', 'important');
        clearBtn.style.setProperty('visibility', 'visible', 'important');
        clearBtn.style.setProperty('opacity', '1', 'important');
      } else {
        clearBtn.style.setProperty('display', 'none', 'important');
      }
    }
  }

  // ─── Observers ───────────────────────────────────────────

  observeFilterClicks() {
    document.addEventListener('click', (e) => {
      // Super Powerful Lock: Ignore clicks on Sorting or Pagination entirely! They don't change the count.
      if (e.target.closest('.gf-sort-wrap, .sort-by, [class*="sort"], .gf-pagination, .pagination')) {
        return;
      }

      if (e.target.closest('.gf-filter-item, .gf-option, .gf-option-box, .gf-clear, .clear-link-button, [onclick*="removeFilterValue"], [onclick*="handleClearAll"]')) {
        this.isFilterChange = true;  // Next updateDisplayCount will recalculate
        this.lockedCount = null;     // Force fresh calculation
        this.hideCounterFor2500ms();
        setTimeout(() => this.toggleClearButton(), 300);
      }
    });

    document.addEventListener('gf_complete', () => {
      this.toggleClearButton();
    });
  }

  /**
   * MutationObserver on product grid.
   * On every DOM change: dispatch empty-state event + reset stable timer.
   * Display count only updates after DOM stops changing for 1.2s.
   */
  observeGridChanges() {
    const productGrid = document.querySelector('.collection') ||
      document.querySelector('[class*="product-grid"]') ||
      document.querySelector('.grid');

    if (!productGrid) return;
    if (this.observedGrid === productGrid && this.gridObserver) return;
    if (this.gridObserver) this.gridObserver.disconnect();

    this.observedGrid = productGrid;
    this.gridObserver = new MutationObserver((mutations) => {
      if (this.isUpdating) return;

      // Ignore mutations on counter elements only (prevents loop)
      const isCounterOnly = mutations.every(m => {
        const t = m.target;
        if (t.classList && (t.classList.contains('gf-summary') || t.classList.contains('gf-filter-result-count') || t.id === 'resultCount')) return true;
        if (t.closest && t.closest('.gf-summary, .gf-filter-result-count, #resultCount')) return true;
        return false;
      });
      if (isCounterOnly) return;

      // Empty-state check (immediate — for GloboEmptyState)
      const cards = this.countVisibleCards();
      window.dispatchEvent(new CustomEvent('globoProductCountUpdate', {
        detail: { visibleCount: cards }
      }));
      
      // Super Powerful Lock: Do not hide counter if it's currently locked to a positive number.
      // (When sorting, DOM cards briefly hit 0, we don't want the counter to vanish/flicker)
      if (cards === 0 && !this.lockedCount) {
        this.lastCount = 0;
        this.hideCounter();
        const rc = document.getElementById('resultCount');
        if (rc) { rc.style.visibility = 'hidden'; rc.style.opacity = '0'; }
      }

      // Reset stable timer — DOM still changing, wait more
      this.scheduleStableCount();
    });

    this.gridObserver.observe(productGrid, { childList: true, subtree: true });

    const tree = document.getElementById('gf-tree');
    if (tree && !this.footerObserver) {
      this.footerObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
           if (mutation.addedNodes.length > 0) {
              const footerMatches = Array.from(mutation.addedNodes).find(
                 node => node.classList && node.classList.contains('gf-filter-footer')
              ) || tree.querySelector('.gf-filter-footer');
              
              if (footerMatches && this.hasActiveFilters()) {
                 let clearBtn = document.querySelector('.gf-custom-clear-all');
                 // If totally gone from memory, recreate a static version quickly
                 if (!clearBtn) {
                    clearBtn = document.createElement("a");
                    clearBtn.className = "gf-custom-clear-all gf-clear-all";
                    clearBtn.innerHTML = "Clear All";
                    clearBtn.addEventListener("click", (e) => {
                      e.preventDefault();
                      if (typeof handleClearAll === 'function') handleClearAll();
                      else if (typeof clearAllFilter === 'function') clearAllFilter();
                      else document.querySelectorAll(".gf-clear, .clear-link-button").forEach(btn => btn.click());
                    });
                 }
                 
                 // Re-inject instantly
                 if (clearBtn.parentElement !== footerMatches) {
                    footerMatches.style.position = 'relative';
                    footerMatches.appendChild(clearBtn);
                 }
                 clearBtn.style.setProperty('display', 'block', 'important');
              }
           }
        }
      });
      // Watch the tree itself for when children are replaced
      this.footerObserver.observe(tree, { childList: true, subtree: true });
    }
  }
}

new GloboProductCounter();